import {
  Button,
  Cascader,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Layout,
  Row,
  Select,
  Table,
  Tooltip,
} from 'antd';
import CustomStep from './lib/CustomStep';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DeleteOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { notifyError, notifySuccessTitle, priceFormat } from '../../store/utils';
import { API } from '../../api';
import {
  districtNos,
  emitter,
  getStoreOrders,
  provinceNos,
  setStoreOrders,
} from '../../store/StoreUtils';
import { useNavigate } from 'react-router-dom';

const { Content } = Layout;

const formItemLayout = {
  labelCol: {
    md: { span: 8 },
  },
  wrapperCol: {
    md: { span: 16 },
  },
};

const formItemLayoutZipCode = {
  labelCol: {
    md: { span: 8 },
  },
  wrapperCol: {
    md: { span: 14 },
  },
};

interface ItemValue {
  key: number;
  no: number;
  itemType: string;
  warningNo: number;
  weightTypeNo: number;
  quantity: number;
  deliveryCost: number;
  additionalCost: number;
  weight: number;
}

const defaultPrice = 5000;
const defaultWeightTypeNo = 1;
const defaultWarningNo = 1;
const defaultDeliveryType = 'HOMEDELIVERY';

const initialItemValue: ItemValue = {
  key: 1,
  no: 0,
  itemType: '',
  warningNo: defaultWarningNo,
  weightTypeNo: defaultWeightTypeNo,
  quantity: 1,
  deliveryCost: defaultPrice,
  additionalCost: 0,
  weight: 0,
};

interface FormData {
  customer: any | null;
  warningList: any[];
  weightTypeList: any[];
  paymentTypeList: any[];
  deliveryTypeList: any[];
}

const OrderCreate = () => {
  const navigate = useNavigate();

  const [form] = Form.useForm();

  const [loading, updateLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [items, setItems] = useState<ItemValue[]>([initialItemValue]);
  const [address, setAddress] = useState([]);

  const [data, setData] = useState<FormData>({
    customer: null,
    warningList: [],
    weightTypeList: [],
    paymentTypeList: [],
    deliveryTypeList: [],
  });

  const senderZipcodes = Form.useWatch('senderZipcodes', form);
  const receiverZipcodes = Form.useWatch('receiverZipcodes', form);

  const fetchFormData = useCallback(async () => {
    try {
      const { result } = await API.get({ apiVersion: 'api' })(`/order/formdata`);
      setData({
        customer: null,
        warningList: result.warningList,
        weightTypeList: result?.weightTypeList,
        paymentTypeList: result?.paymentTypeList || [],
        deliveryTypeList: result?.deliveryTypeList || [],
      });
    } catch (error: any) {
      notifyError(error.message);
    }
  }, []);

  const fetchFullAddress = useCallback(async () => {
    try {
      const { result } = await API.get({ apiVersion: 'api' })(`/address/full`);
      setAddress(result?.data || []);
    } catch (error: any) {
      notifyError(error.message);
    }
  }, []);

  const onFinish = useCallback(
    async (values: any) => {
      try {
        updateLoading(true);
        let payload: any = {
          deliveryType: defaultDeliveryType,
          targetDate: null,

          customerNo: null,
          senderFname: values.senderFname,
          senderPhone1: values.senderPhone1,
          senderPhone2: values.senderPhone2,
          senderDetail: values.senderDetail,
          senderW3w: values.senderW3w,
          senderStreetNo: values.senderZipcodes.at(-1),

          receiverFname: values?.receiverFname,
          receiverPhone1: values?.receiverPhone1,
          receiverPhone2: values?.receiverPhone2 || undefined,
          receiverDetail: values?.receiverDetail,
          receiverW3w: values?.receiverW3w,
          receiverStreetNo: values?.receiverZipcodes.at(-1),
          paymentTypeCd: 'PRE_PAID',
        };

        let listItems = items.map((item) => {
          return {
            no: item.no,
            itemType: item.itemType,
            weightTypeNo: item.weightTypeNo,
            warningNo: item.warningNo,
            quantity: item.quantity,
            deliveryCost: item.deliveryCost,
            additionalCost: item.additionalCost,
            weight: item.weight,
          };
        });

        payload.items = listItems;
        const {
          result: { data },
        } = await API.post({ apiVersion: 'api' })(`/customer-order`, {
          orders: [payload],
        });
        if (data) {
          navigate(`/customer-order/payment/${data?.no}`);
        }
      } catch (error: any) {
        notifyError(error.message);
      } finally {
        updateLoading(false);
      }
    },
    [items]
  );

  const saveOrder = useCallback(async () => {
    try {
      setSaveLoading(true);
      const values = await form.validateFields();
      const orders = await getStoreOrders();

      const senderZipcodeNames: string[] = [];
      const receiverZipcodeNames: string[] = [];
      address.forEach((provinces: any) => {
        if (provinces.no === values.senderZipcodes?.[0]) {
          senderZipcodeNames.push(provinces?.name);
          (provinces.districts || []).forEach((district: any) => {
            if (district.no === values.senderZipcodes?.[1]) {
              senderZipcodeNames.push(district?.name);
              (district.streets || []).forEach((street: any) => {
                if (street.no === values.senderZipcodes?.[2]) {
                  senderZipcodeNames.push(street?.name);
                }
              });
            }
          });
        }
        if (provinces.no === values.receiverZipcodes?.[0]) {
          receiverZipcodeNames.push(provinces?.name);
          (provinces.districts || []).forEach((district: any) => {
            if (district.no === values.receiverZipcodes?.[1]) {
              receiverZipcodeNames.push(district?.name);
              (district.streets || []).forEach((street: any) => {
                if (street.no === values.receiverZipcodes?.[2]) {
                  receiverZipcodeNames.push(street?.name);
                }
              });
            }
          });
        }
      });

      let payload: any = {
        deliveryType: defaultDeliveryType,
        targetDate: null,

        customerNo: null,
        senderFname: values.senderFname,
        senderPhone1: values.senderPhone1,
        senderPhone2: values.senderPhone2,
        senderDetail: values.senderDetail,
        senderW3w: values.senderW3w,
        senderStreetNo: values.senderZipcodes,
        senderZipcodeNames,

        receiverFname: values?.receiverFname,
        receiverPhone1: values?.receiverPhone1,
        receiverPhone2: values?.receiverPhone2 || undefined,
        receiverDetail: values?.receiverDetail,
        receiverW3w: values?.receiverW3w,
        receiverStreetNo: values?.receiverZipcodes,
        receiverZipcodeNames,
        paymentTypeCd: 'PRE_PAID',
      };

      let listItems = items.map((item) => {
        let warning = data.warningList.find((warning) => warning.no === item.warningNo);
        let weightType = data.weightTypeList.find(
          (weightType) => weightType.no === item.weightTypeNo
        );
        return {
          no: item.no,
          itemType: item.itemType,
          weightTypeNo: item.weightTypeNo,
          weightTypeName: (weightType || {}).name || '',
          warningNo: item.warningNo,
          warningName: (warning || {}).name || '',
          quantity: item.quantity,
          deliveryCost: item.deliveryCost,
          additionalCost: item.additionalCost,
          weight: item.weight,
        };
      });

      payload.items = listItems;

      console.log(payload);

      orders.push(payload);

      await setStoreOrders(orders);

      form.resetFields([
        'receiverFname',
        'receiverPhone1',
        'receiverPhone2',
        'receiverW3w',
        'receiverZipcodes',
        'receiverDetail',
      ]);

      setItems([initialItemValue]);

      emitter.emit('localOrderChange');

      notifySuccessTitle('Захиалгыг амжилттай хадгаллаа', 'Дараагийн захиалгаа оруулна уу');
    } catch (error: any) {
      notifyError(error.message || 'Мэдээлэл бүрэн оруулнау уу!');
    } finally {
      setSaveLoading(false);
    }
  }, [items, data, form, address]);

  useEffect(() => {
    if (senderZipcodes && receiverZipcodes) {
      calculateAmount(items);
    }
  }, [senderZipcodes, receiverZipcodes]);

  useEffect(() => {
    fetchFormData();
    fetchFullAddress();
  }, []);

  const onChangeItemValue = useCallback(
    (key: number, value: any, name: string) => {
      const list = items.map((item: any) => {
        if (item.key === key) {
          return {
            ...item,
            [name]: value,
          };
        } else {
          return item;
        }
      });
      if (name === 'weightTypeNo') {
        calculateAmount(list);
      } else {
        setItems(list);
      }
    },
    [items]
  );

  const calculateAmount = useCallback(
    async (list: any[]) => {
      try {
        const values = form.getFieldsValue();
        const weightTypeNoList = list.map((item: ItemValue) => item.weightTypeNo);
        if (
          values?.senderZipcodes &&
          values.senderZipcodes.length &&
          values?.receiverZipcodes &&
          values.receiverZipcodes.length
        ) {
          const payload = {
            customerNo: values.customerNo,
            senderStreetNo: values.senderZipcodes.at(-1),
            receiverStreetNo: values.receiverZipcodes.at(-1),
            weightTypeNoList,
            deliveryType: defaultDeliveryType,
          };

          const { result } = await API.post({ apiVersion: 'api' })(
            `/order/calculate/cost`,
            payload
          );
          const newItems = list.map((item: any) => {
            const price = result.find((p: any) => p.weightTypeNo === item.weightTypeNo) || {
              amount: defaultPrice,
            };
            return {
              ...item,
              deliveryCost: price?.amount,
            };
          });
          setItems(newItems);
        } else {
          const newItems = list.map((item: any) => {
            return {
              ...item,
              deliveryCost: defaultPrice,
            };
          });
          setItems(newItems);
        }
      } catch (error: any) {
        notifyError(error.message);
      }
    },
    [form, items]
  );

  const removeOrderLineTime = useCallback(
    (key: number) => {
      const list = items.filter((item: any) => item.key !== key);
      setItems(list);
    },
    [items]
  );

  const addOrderLineItem = useCallback(() => {
    let lastItem = items[items.length - 1];
    let lineItem = {
      key: lastItem.key + 1,
      no: 0,
      itemType: '',
      warningNo: defaultWarningNo,
      weightTypeNo: defaultWeightTypeNo,
      quantity: 1,
      deliveryCost: defaultPrice,
      additionalCost: 0,
      weight: 0,
    };
    let newItems = [...items, lineItem];
    calculateAmount(newItems);
  }, [items]);

  const totalAmount = useMemo(() => {
    const total = items.reduce(
      (accumulator, currentValue) =>
        accumulator +
        (currentValue.deliveryCost + currentValue.additionalCost) * currentValue.quantity,
      0
    );
    return total;
  }, [items]);

  const options = useMemo(() => {
    const items = address.map((item: any) => {
      return {
        value: item.no,
        label: item.name,
        disabled: provinceNos.indexOf(item.no) < 0,
        children: (item.districts || []).map((district: any) => {
          return {
            value: district.no,
            label: district.name,
            disabled: districtNos.indexOf(district.no) < 0,
            children: (district.streets || []).map((street: any) => {
              return {
                value: street.no,
                label: street.name,
              };
            }),
          };
        }),
      };
    });
    return items;
  }, [address, provinceNos, districtNos]);

  const receiverOptions = useMemo(() => {
    const items = address.map((item: any) => {
      return {
        value: item.no,
        label: item.name,
        children: (item.districts || []).map((district: any) => {
          return {
            value: district.no,
            label: district.name,
            children: (district.streets || []).map((street: any) => {
              return {
                value: street.no,
                label: street.name,
              };
            }),
          };
        }),
      };
    });
    return items;
  }, [address]);

  const columns = useMemo(() => {
    return [
      {
        title: '#',
        width: 50,
        key: 'Number',
        render: (val: any, row: any) => <span>{items.indexOf(row) + 1}</span>,
      },
      {
        title: 'Ил.Төрөл',
        dataIndex: 'itemType',
        key: 'itemType',
        render: (val: any, row: any) => (
          <Input
            placeholder="Илгээмжийн төрөл"
            value={val}
            onChange={(event: any) => onChangeItemValue(row.key, event.target.value, 'itemType')}
          />
        ),
      },
      {
        title: 'Анхаарах',
        dataIndex: 'warningNo',
        key: 'warningNo',
        render: (val: any, row: any) => (
          <Select
            style={{ width: '100%' }}
            placeholder="Анхаарах"
            defaultValue={defaultWarningNo}
            value={val}
            onChange={(val) => onChangeItemValue(row.key, val, 'warningNo')}
          >
            {data.warningList.map((item: any) => (
              <Select.Option key={item.no} value={item.no}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        title: 'Хэмжээ',
        dataIndex: 'weightTypeNo',
        key: 'weightTypeNo',
        width: 100,
        render: (val: any, row: any) => (
          <Select
            style={{ width: '100%' }}
            placeholder="Хэмжээ"
            defaultValue={defaultWeightTypeNo}
            value={val}
            onChange={(val) => onChangeItemValue(row.key, val, 'weightTypeNo')}
          >
            {data.weightTypeList.map((item: any) => (
              <Select.Option key={item.no} value={item.no}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        title: 'Тоо.ш',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 80,
        render: (val: any, row: any) => (
          <div>
            <InputNumber
              min={1}
              step={1}
              defaultValue={1}
              value={val}
              onChange={(val) => onChangeItemValue(row.key, val, 'quantity')}
            />
          </div>
        ),
      },
      {
        title: 'Нэгж үнэ',
        key: 'deliveryCost',
        dataIndex: 'deliveryCost',
        width: 80,
        render: (val: any) => priceFormat(val),
      },
      {
        title: 'Нэмэгдэл',
        dataIndex: 'additionalCost',
        key: 'additionalCost',
        width: 60,
        render: (val: any, row: any) => (
          <InputNumber
            min={0}
            step={100}
            defaultValue={0}
            value={val}
            onChange={(val) => onChangeItemValue(row.key, val, 'additionalCost')}
          />
        ),
      },
      {
        title: 'Жин',
        dataIndex: 'weight',
        key: 'weight',
        width: 60,
        render: (val: any, row: any) => (
          <InputNumber
            min={0}
            step={100}
            defaultValue={0}
            value={val}
            onChange={(val) => onChangeItemValue(row.key, val, 'weight')}
          />
        ),
      },
      {
        title: 'Нийт',
        width: 80,
        key: 'totalCost',
        render: (val: any, row: any) => (
          <span>{priceFormat((row.deliveryCost + row.additionalCost) * row.quantity)} </span>
        ),
      },
      {
        title: 'Үйлд.',
        key: 'Action',
        width: 60,
        render: (val: any, row: any) => (
          <div>
            {items.length > 1 ? (
              <Button
                danger
                type="primary"
                icon={<DeleteOutlined />}
                size="small"
                onClick={() => removeOrderLineTime(row.key)}
              ></Button>
            ) : null}
          </div>
        ),
      },
    ];
  }, [items, data]);

  return (
    <Content className="custom-container custom-order-content">
      <CustomStep current={0} />
      <Row>
        <Col
          lg={{ span: 18, offset: 3 }}
          xs={{ span: 18, offset: 3 }}
          md={{ span: 18, offset: 0 }}
          sm={{ span: 24, offset: 0 }}
        >
          <Form form={form} onFinish={onFinish}>
            <Row gutter={24}>
              <Col span={12} className="sender-title">
                <Form.Item
                  colon={false}
                  {...formItemLayout}
                  label="Илгээгч"
                  style={{ marginBottom: 10, fontSize: 24, fontWeight: 'bold' }}
                >
                  {/* <Button type="primary" size="small"
                    onClick={() => this.setState({ senderAddress: true })}>Хаяг
                    сонгох</Button> */}
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  {...formItemLayout}
                  label="Нэр"
                  style={{ marginBottom: 10 }}
                  name="senderFname"
                  rules={[{ required: true, message: 'Нэр оруулна уу!' }]}
                >
                  <Input placeholder="Илгээгч нэр" />
                </Form.Item>
                <Row>
                  <Col span={16}>
                    <Form.Item
                      labelCol={{ md: { span: 12 } }}
                      wrapperCol={{ md: { span: 12 } }}
                      label="Утас"
                      style={{ marginBottom: 10 }}
                      name="senderPhone1"
                      rules={[{ required: true, message: 'Утас 1 оруулна уу!' }]}
                    >
                      <Input placeholder="Утас 1" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item style={{ marginBottom: 10, paddingLeft: 10 }} name="senderPhone2">
                      <Input placeholder="Утас 2" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={15}>
                  <Col span={24} className="sender-address-container">
                    <Form.Item
                      {...formItemLayoutZipCode}
                      name="senderZipcodes"
                      rules={[{ required: true, message: 'Зип код оруулна уу!' }]}
                      label="Зип код"
                    >
                      <Cascader options={options} placeholder="Илгээгчийн хаяг" />
                    </Form.Item>
                    <Tooltip
                      className="tool-help"
                      title="Онлайнаар зөвхөн Улаанбаатар-Орон нутгийн чиглэлд илгээх шуудангийн захиалга авч байна. Орон нутгаас Улаанбаатар хотруу шуудан илгээх хэрэглэгч аймгийн шуудангийн салбарт хандана уу."
                    >
                      <QuestionCircleOutlined style={{ fontSize: 18 }} />
                    </Tooltip>
                  </Col>
                </Row>
              </Col>
              <Col span={12}>
                <Form.Item
                  {...formItemLayout}
                  label="Хаяг дэлгэрэнгүй"
                  style={{ marginBottom: 10 }}
                  rules={[{ required: true, message: 'Хаяг дэлгэрэнгүй оруулна уу!' }]}
                  name="senderDetail"
                >
                  <Input.TextArea rows={3} placeholder="Илгээгчийн дэлгэрэнгүй хаяг" />
                </Form.Item>
                <Form.Item
                  {...formItemLayout}
                  label="what3words"
                  style={{ marginBottom: 10 }}
                  name="senderW3w"
                >
                  <Input placeholder="what3words" />
                </Form.Item>
              </Col>
            </Row>
            <Divider style={{ marginTop: 10, marginBottom: 15 }} />
            <Row gutter={24}>
              <Col span={12} className="receiver-title">
                <Form.Item
                  colon={false}
                  {...formItemLayout}
                  label="Хүлээн авагч"
                  style={{ marginBottom: 10, fontSize: 24, fontWeight: 'bold' }}
                >
                  {/* <Button type="primary" size="small"
                    onClick={() => this.setState({ receiverAddress: true })}>Хаяг
                    сонгох</Button> */}
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  {...formItemLayout}
                  label="Нэр"
                  style={{ marginBottom: 10 }}
                  name="receiverFname"
                  rules={[{ required: true, message: 'Нэр оруулна уу!' }]}
                >
                  <Input placeholder="Хүлээн авагчийн нэр" />
                </Form.Item>
                <Row>
                  <Col span={16}>
                    <Form.Item
                      labelCol={{ md: { span: 12 } }}
                      wrapperCol={{ md: { span: 12 } }}
                      label="Утас"
                      style={{ marginBottom: 10 }}
                      rules={[{ required: true, message: 'Утас 1 оруулна уу!' }]}
                      name="receiverPhone1"
                    >
                      <Input placeholder="Утас 1" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item style={{ marginBottom: 10, paddingLeft: 10 }} name="receiverPhone2">
                      <Input placeholder="Утас 2" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  {...formItemLayout}
                  label="Зип код"
                  style={{ marginBottom: 10 }}
                  name="receiverZipcodes"
                  rules={[{ required: true, message: 'Зип код оруулна уу!' }]}
                >
                  <Cascader options={receiverOptions} placeholder="Хүлээн авагчийн хаяг" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  {...formItemLayout}
                  label="Хаяг дэлгэрэнгүй"
                  style={{ marginBottom: 10 }}
                  name="receiverDetail"
                  rules={[{ required: true, message: 'Хаяг дэлгэрэнгүй оруулна уу!' }]}
                >
                  <Input.TextArea rows={3} placeholder="Хүлээн дэлгэрэнгүй хаяг" />
                </Form.Item>
                <Form.Item
                  {...formItemLayout}
                  label="what3words"
                  style={{ marginBottom: 10 }}
                  name="receiverW3w"
                >
                  <Input placeholder="what3words" />
                </Form.Item>
              </Col>
            </Row>
            <Divider style={{ marginTop: 10, marginBottom: 15 }} />
            <Row gutter={24} style={{ marginTop: 20, marginBottom: 20 }}>
              <Col span={24}>
                <div className="order-item-table-wrapper">
                  <Table bordered={true} pagination={false} columns={columns} dataSource={items} />
                </div>
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <Button color="primary" variant="link" onClick={addOrderLineItem}>
                  <PlusOutlined /> Хайрцаг нэмэх
                </Button>
              </Col>
              <Col span={12}>
                <span style={{ fontSize: 18, fontWeight: 'bold', float: 'right' }}>
                  {' '}
                  Нийт төлбөр: {priceFormat(totalAmount)}₮
                </span>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={24} style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                <Button
                  type="default"
                  style={{ marginRight: 10 }}
                  loading={saveLoading}
                  disabled={loading}
                  onClick={() => saveOrder()}
                  name={'order'}
                  value={'addOrder'}
                >
                  Захиалга нэмэх
                </Button>
                <Button type="primary" htmlType="submit" disabled={saveLoading} loading={loading}>
                  Шууд төлөх
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </Content>
  );
};

export default OrderCreate;
